import React, { useState, useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { appColors } from '../Config/Theme';
import { helpTopics } from '../help/helpTopics';
import { searchHelpTopics } from '../help/helpSearch';
import type { HelpCategory, HelpAudience } from '../help/types';
import HelpSearch from '../Components/Help/HelpSearch';
import HelpCategorySection from '../Components/Help/HelpCategorySection';
import HelpTopicCard from '../Components/Help/HelpTopicCard';

const CATEGORIES: HelpCategory[] = ['Quick Start', 'Core Workflow', 'Reference', 'Troubleshooting'];
const AUDIENCES: HelpAudience[] = ['Incident Commander', 'Headcounter', 'Admin', 'Viewer'];

const HelpLandingPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | 'All'>('All');
  const [selectedAudience, setSelectedAudience] = useState<HelpAudience | 'All'>('All');

  const filteredTopics = useMemo(() => {
    let results = search.trim() ? searchHelpTopics(search) : helpTopics;

    if (selectedCategory !== 'All') {
      results = results.filter((t) => t.category === selectedCategory);
    }
    if (selectedAudience !== 'All') {
      results = results.filter((t) => t.audience.includes(selectedAudience));
    }
    return results;
  }, [search, selectedCategory, selectedAudience]);

  const isSearching = search.trim().length > 0;
  const hasResults = filteredTopics.length > 0;

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Help Center
        </Typography>
        <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.5 }}>
          Learn how to use EEAS during evacuation and accountability workflows.
        </Typography>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <HelpSearch value={search} onChange={setSearch} />
      </Box>

      {/* Category & audience filter chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {(['All', ...CATEGORIES] as const).map((cat) => (
          <Chip
            key={cat}
            label={cat}
            size="small"
            onClick={() => setSelectedCategory(cat)}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              borderRadius: '14px',
              bgcolor: selectedCategory === cat ? appColors.primary.main : appColors.surface.card,
              color: selectedCategory === cat ? '#fff' : appColors.text.body,
              border: `1px solid ${selectedCategory === cat ? appColors.primary.main : appColors.border.default}`,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: selectedCategory === cat ? appColors.primary.dark : appColors.surface.inactive,
              },
            }}
          />
        ))}
        <Box sx={{ width: 8 }} />
        {(['All', ...AUDIENCES] as const).map((aud) => (
          <Chip
            key={aud}
            label={aud}
            size="small"
            onClick={() => setSelectedAudience(aud)}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              borderRadius: '14px',
              bgcolor: selectedAudience === aud ? appColors.primary.main : appColors.surface.card,
              color: selectedAudience === aud ? '#fff' : appColors.text.body,
              border: `1px solid ${selectedAudience === aud ? appColors.primary.main : appColors.border.default}`,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: selectedAudience === aud ? appColors.primary.dark : appColors.surface.inactive,
              },
            }}
          />
        ))}
      </Box>

      {/* Results */}
      {!hasResults && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" sx={{ color: appColors.text.caption, fontWeight: 600 }}>
            No help topics found
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body, mt: 1 }}>
            Try a different search term or clear the filters.
          </Typography>
        </Box>
      )}

      {isSearching && hasResults ? (
        /* Search results — flat list */
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {filteredTopics.map((topic) => (
            <HelpTopicCard key={topic.id} topic={topic} />
          ))}
        </Box>
      ) : (
        /* Grouped by category */
        hasResults && (
          <>
            {CATEGORIES.map((cat) => {
              const topicsInCat = filteredTopics.filter((t) => t.category === cat);
              return <HelpCategorySection key={cat} title={cat} topics={topicsInCat} />;
            })}
          </>
        )
      )}
    </Box>
  );
};

export default HelpLandingPage;
